import React from "react";
import { Warehouse, FileBox, Truck } from "lucide-react";
import { RoleHelper } from "src/helpers";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { ActiveAntsPurchaseOrder } from "src/types";
import { useAuthorisation } from "src/context";
import { StatusPill } from "src/components/ui";
import { CollapsibleSection } from "src/components/ui/collapsible";
import {
  CheckInHistoryList,
  CheckInProductReceiptsTable,
  CheckInFlaggedIssuesPanel
} from "src/components/sections/warehouseCheckIns";

interface ActiveAntsPurchaseOrderWarehouseSectionProps {
  activeAntsPurchaseOrder: ActiveAntsPurchaseOrder;
}

export const ActiveAntsPurchaseOrderWarehouseSection: React.FC<ActiveAntsPurchaseOrderWarehouseSectionProps> = ({
  activeAntsPurchaseOrder
}) => {
  const { userRole } = useAuthorisation();
  const isHealfTeam = RoleHelper.isHealfTeam(userRole);

  const {
    id: activeAntsPurchaseOrderId,
    inboundPackingSlipId,
    checkInStatus,
    checkInTimestamps,
    checkInFlaggedIssues,
    products = []
  } = activeAntsPurchaseOrder;

  if (
    !inboundPackingSlipId &&
    !checkInStatus &&
    (!checkInTimestamps || checkInTimestamps.length === 0) &&
    products.length === 0
  ) {
    return null;
  }

  const hasDiscrepancyToShow = (checkInFlaggedIssues?.length ?? 0) > 0 || checkInStatus === "Resolved";
  const statusPillLink =
    isHealfTeam && hasDiscrepancyToShow
      ? `${ROUTES_CONFIG.activeAntsReceivingDiscrepancies.path}?id=${activeAntsPurchaseOrderId}`
      : undefined;

  const buildCheckInLink = isHealfTeam
    ? (timestamp: string) =>
        `${ROUTES_CONFIG.activeAntsWarehouseCheckIn.path}?id=${activeAntsPurchaseOrderId}&timestamp=${timestamp}`
    : undefined;

  return (
    <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
      <CollapsibleSection
        initiallyOpen={false}
        title={
          <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
            <Warehouse className="w-5 h-5 mr-2 text-primary-600" />
            Warehouse & Check-In
          </h3>
        }
      >
        <div className="p-6 space-y-6">
          {checkInStatus && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-secondary-700">Check-In Status</span>
              <StatusPill status={checkInStatus} linkPath={statusPillLink} />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-secondary-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Truck className="w-4 h-4 text-primary-600" />
                <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                  Warehouse Company
                </span>
              </div>
              <p className="text-sm font-medium text-secondary-900">Active Ants</p>
            </div>
            {inboundPackingSlipId !== undefined && (
              <div className="bg-secondary-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FileBox className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                    Inbound Packing Slip
                  </span>
                </div>
                <p className="text-sm font-medium text-secondary-900">{inboundPackingSlipId}</p>
              </div>
            )}
          </div>

          {checkInStatus && <CheckInHistoryList checkInTimestamps={checkInTimestamps} buildLink={buildCheckInLink} />}

          {products.length > 0 && (
            <CheckInProductReceiptsTable products={products} hasCheckIns={!!checkInTimestamps?.length} />
          )}

          <CheckInFlaggedIssuesPanel flaggedIssues={checkInFlaggedIssues} />
        </div>
      </CollapsibleSection>
    </div>
  );
};
